import React, { useState, useEffect, useRef, useMemo } from "react";

import { useProfil } from "../../contexts/ProfilContext";
import { useAuth } from "../../hooks/useAuth";
import FormulaireInscription from "../FormulaireInscription";
import ResetPasswordView from "./ResetPasswordView";
import Button from "../UI/Button";
import Input from "../UI/Input";
import { trackFunnelEvent } from "../../utils/trackFunnelEvent";
import { formatCHF } from "../../utils/formatCHF";
import AccountAccordionCard from "../Account/AccountAccordionCard";
import {
  normalizeOrderTotalCents,
  getOrderStatusLabel,
  getOrderPaymentNotice,
  isPaidStatus,
  isUnpaidStatus,
} from "../../utils/orderPayment";

/**
 * ============================================================
 * Configuration API
 * ============================================================
 *
 * Ce bloc centralise la base des endpoints PHP utilisés
 * par la vue compte.
 */
const API_BASE = "https://marchedeluxe.ch/services";

/**
 * ============================================================
 * Détection d’un item plateau
 * ============================================================
 *
 * Ce bloc reconnaît un plateau configuré dans l’historique.
 */
const isPlateauOrderItem = (item) => {
  if (!item || typeof item !== "object") return false;

  const sourceTable = String(item?.source_table || "")
    .trim()
    .toLowerCase();

  const itemType = String(item?.item_type || item?.type || "")
    .trim()
    .toLowerCase();

  const typeProduit = String(item?.typeProduit || "")
    .trim()
    .toLowerCase();

  if (sourceTable === "virtual_platter") return true;
  if (itemType === "platter" || itemType === "plateau") return true;
  if (typeProduit === "plateau-evenement") return true;

  return Boolean(
    item?.isPlateau ||
      item?.isPlatter ||
      item?.isConfiguredPlateau ||
      item?.isConfiguredPlatter ||
      item?.isVirtualPlatter
  );
};

/**
 * ============================================================
 * Détection d’un item produit simple
 * ============================================================
 *
 * Ce bloc sert à distinguer les produits ré-ajoutables directement.
 */
const isSimpleReorderableItem = (item) => {
  if (!item || typeof item !== "object") return false;
  if (isPlateauOrderItem(item)) return false;

  const id = Number(item?.id ?? item?.product_id ?? item?.productId ?? 0);
  return Number.isFinite(id) && id > 0;
};

/**
 * ============================================================
 * Capacités de réachat d’une commande
 * ============================================================
 *
 * Ce bloc résume ce qui peut être rejoué dans une commande :
 * - produits simples
 * - plateau
 * - premier plateau exploitable
 */
const getOrderReorderCapabilities = (items = []) => {
  const safeItems = Array.isArray(items) ? items : [];

  const simpleItems = safeItems.filter((item) => isSimpleReorderableItem(item));
  const plateauItems = safeItems.filter((item) => isPlateauOrderItem(item));

  return {
    hasSimpleItems: simpleItems.length > 0,
    hasPlateauItems: plateauItems.length > 0,
    hasAnyReorderable: simpleItems.length > 0 || plateauItems.length > 0,
    simpleItems,
    plateauItems,
    firstPlateauItem: plateauItems[0] || null,
    simpleCount: simpleItems.length,
    plateauCount: plateauItems.length,
  };
};

/**
 * ============================================================
 * Message métier pour une commande prête
 * ============================================================
 *
 * Ce bloc convertit certains statuts backend en message
 * lisible côté client.
 */
const getPreparationNotice = (order, t = {}) => {
  const status = String(order?.status || "")
    .trim()
    .toLowerCase();

  if (status === "ready_for_pick") {
    return (
      t.orderReadyForPick ||
      "Votre commande est prête et peut être retirée en magasin."
    );
  }

  if (status === "ready_for_delivery") {
    return (
      t.orderReadyForDelivery || "Votre commande est prête pour la livraison."
    );
  }

  if (status === "ready") {
    return t.orderReadyGeneric || "Votre commande est prête.";
  }

  return "";
};

/**
 * ============================================================
 * Style du message de commande prête
 * ============================================================
 *
 * Ce bloc fournit le style inline du panneau d’information
 * affiché lorsqu’une commande est prête.
 */
const getPreparationNoticeStyle = () => ({
  marginBottom: "14px",
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid rgba(39,174,96,.22)",
  background: "rgba(39,174,96,.08)",
  color: "#1f6f43",
  lineHeight: 1.5,
  fontSize: "0.92rem",
});

/**
 * ============================================================
 * Extraction de date SQL
 * ============================================================
 *
 * Ce bloc extrait la partie YYYY-MM-DD d’une date SQL/ISO.
 * Il sert au filtre date des commandes.
 */
const extractSqlDate = (raw) => {
  if (!raw) return "";
  return String(raw).slice(0, 10);
};

/**
 * ============================================================
 * Résumé de la carte commandes
 * ============================================================
 *
 * Ce bloc fabrique le texte de synthèse affiché
 * quand la carte historique est fermée.
 */
const buildOrdersPreview = (orders = [], t = {}) => {
  if (!Array.isArray(orders) || orders.length === 0) {
    return t.noOrdersYet || "Aucune commande pour le moment.";
  }

  const latest = orders[0];
  const latestId = latest?.id ? `#${latest.id}` : "—";

  return `${orders.length} commande(s) • Dernière ${latestId}`;
};

/**
 * ============================================================
 * Composant principal du compte client
 * ============================================================
 *
 * Ce composant gère :
 * - la connexion
 * - l’inscription
 * - le mot de passe oublié
 * - l’édition du profil
 * - l’historique des commandes
 * - la reprise de paiement
 * - la suppression de commandes non payées
 * - le réachat rapide depuis l’historique
 */
const AccountView = ({
  t = {},
  onLogoutRequest,
  onReorderSimpleProduct,
  onReconfigurePlateau,
}) => {
  /**
   * ============================================================
   * Contextes globaux
   * ============================================================
   *
   * Ce bloc récupère :
   * - le profil utilisateur
   * - l’état d’authentification
   * - les actions login / logout
   */
  const { userProfil, authStatus, setUserProfil } = useProfil();
  const { login, logout, authStatus: fetchStatus } = useAuth();

  /**
   * ============================================================
   * États d’interface
   * ============================================================
   *
   * Ce bloc pilote :
   * - l’inscription
   * - le mot de passe oublié
   * - l’édition profil
   * - l’ouverture d’une commande
   * - la suppression d’une commande
   * - l’ouverture des cartes accordéon
   */
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [isDeletingOrderId, setIsDeletingOrderId] = useState(null);

  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [showOrdersSection, setShowOrdersSection] = useState(false);

  /**
   * ============================================================
   * État du filtre date des commandes
   * ============================================================
   *
   * Ce bloc conserve la date choisie dans la carte historique.
   */
  const [selectedOrderDate, setSelectedOrderDate] = useState("");

  /**
   * ============================================================
   * États de données
   * ============================================================
   *
   * Ce bloc conserve :
   * - la liste des commandes
   * - les données éditables du profil
   * - le message d’erreur de la vue
   */
  const [commandes, setCommandes] = useState([]);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState("");

  /**
   * ============================================================
   * Feedback local de recommandation
   * ============================================================
   *
   * Ce bloc conserve un message local à une commande
   * pour éviter un feedback trop loin du clic utilisateur.
   */
  const [reorderFeedbackByOrder, setReorderFeedbackByOrder] = useState({});

  /**
   * ============================================================
   * Formulaire de connexion
   * ============================================================
   *
   * Ce bloc contient les champs email / mot de passe
   * utilisés par le formulaire de connexion.
   */
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  /**
   * ============================================================
   * Formulaire mot de passe oublié
   * ============================================================
   *
   * Ce bloc contient :
   * - l’email
   * - le message de retour
   * - l’état de chargement
   */
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  /**
   * ============================================================
   * Référence de la section commandes
   * ============================================================
   *
   * Ce bloc sert si l’on veut scroller ou cibler
   * la carte historique.
   */
  const ordersRef = useRef(null);

  /**
   * ============================================================
   * Détection du token de reset
   * ============================================================
   *
   * Ce bloc lit l’URL pour savoir si la vue doit basculer
   * vers la réinitialisation de mot de passe.
   */
  const searchParams = new URLSearchParams(window.location.search);
  const resetToken = searchParams.get("reset_token") || "";
  /**
 * ============================================================
 * Funnel inscription — ouverture formulaire
 * ============================================================
 *
 * Ce bloc trace l’ouverture du formulaire d’inscription.
 */
useEffect(() => {
  if (isRegistering) {
    trackFunnelEvent("signup_open");
  }
}, [isRegistering]);

  /**
   * ============================================================
   * Formatage d’une date de commande
   * ============================================================
   *
   * Ce bloc convertit une date SQL/ISO vers un affichage fr-CH.
   */
  const formatOrderDate = (raw) => {
    if (!raw) return "";

    const iso =
      typeof raw === "string" && raw.includes(" ")
        ? raw.replace(" ", "T")
        : raw;

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(raw);

    return d.toLocaleString("fr-CH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * ============================================================
   * Timestamp de création d’une commande
   * ============================================================
   *
   * Ce bloc convertit la date d’une commande en timestamp
   * pour les comparaisons et le tri.
   */
  const getOrderCreatedAtMs = (order) => {
    const raw = order?.created_at || order?.date_creation || "";
    if (!raw) return 0;

    const iso =
      typeof raw === "string" && raw.includes(" ")
        ? raw.replace(" ", "T")
        : raw;

    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };

  /**
   * ============================================================
   * Règle d’affichage du bouton supprimer
   * ============================================================
   *
   * Ce bloc autorise l’affichage du bouton supprimer
   * uniquement pour une commande non payée âgée d’au moins 3 jours.
   */
  const canClientDeleteOrder = (order) => {
    if (!isUnpaidStatus(order?.status)) return false;

    const createdAtMs = getOrderCreatedAtMs(order);
    if (!createdAtMs) return false;

    const threeDaysMs = 3 * 24 * 60 * 60 * 1000;
    return Date.now() - createdAtMs >= threeDaysMs;
  };

  /**
   * ============================================================
   * Construction des headers authentifiés
   * ============================================================
   *
   * Ce bloc prépare les headers fetch avec ou sans
   * Content-Type JSON.
   */
  const authHeaders = (isPost = false) => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("user_token") || ""}`,
    };

    if (isPost) {
      headers["Content-Type"] = "application/json";
    }

    return headers;
  };

  /**
   * ============================================================
   * Chargement du profil SQL
   * ============================================================
   *
   * Ce bloc interroge le backend pour récupérer
   * les coordonnées du client connecté.
   */
  const fetchProfilSql = async () => {
    const res = await fetch(`${API_BASE}/get_profil.php`, {
      headers: authHeaders(),
    });

    if (res.status === 401) {
      logout();
      throw new Error("Session expirée, veuillez vous reconnecter.");
    }

    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || "Profil introuvable");
    }

    return data.user;
  };

  /**
   * ============================================================
   * Chargement des commandes SQL
   * ============================================================
   *
   * Ce bloc interroge le backend pour récupérer
   * l’historique des commandes du client connecté.
   */
  const fetchOrdersSql = async () => {
    const res = await fetch(`${API_BASE}/get_orders.php`, {
      headers: authHeaders(),
    });

    if (res.status === 401) {
      logout();
      throw new Error("Session expirée, veuillez vous reconnecter.");
    }

    const data = await res.json();
    if (!data.success) {
      throw new Error(data.message || "Commandes introuvables");
    }

    return data.orders || [];
  };

  /**
   * ============================================================
   * Inscription utilisateur
   * ============================================================
   *
   * Ce bloc envoie les données du formulaire d’inscription
   * puis hydrate le contexte si la création réussit.
   */
  const registerSql = async (payload) => {
  setError("");

  trackFunnelEvent("signup_submit", {
    has_email: Boolean(payload?.email),
    has_phone: Boolean(payload?.telephone),
    has_firstname: Boolean(payload?.prenom),
    has_lastname: Boolean(payload?.nom),
  });

  try {
    const res = await fetch(`${API_BASE}/inscription.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.success) {
      trackFunnelEvent("signup_error", {
        status: res.status,
        message: data?.message || "Erreur inscription",
      });

      throw new Error(data?.message || `Erreur inscription (${res.status})`);
    }

    trackFunnelEvent("signup_success", {
      has_token: Boolean(data?.token),
      has_user: Boolean(data?.user),
    });

    if (data?.token) {
      localStorage.setItem("user_token", data.token);
    }

    if (data?.user) {
      setUserProfil(data.user);
    }

    setIsRegistering(false);
    return data.user;
  } catch (err) {
    trackFunnelEvent("signup_error", {
      message: err?.message || "Erreur réseau inscription",
    });

    throw err;
  }
};

  /**
   * ============================================================
   * Mot de passe oublié
   * ============================================================
   *
   * Ce bloc déclenche l’envoi du lien de réinitialisation.
   */
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotMessage("");
    setForgotLoading(true);

    try {
      const res = await fetch(`${API_BASE}/forgot_password.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.success) {
        throw new Error(
          data?.message || "Impossible d'envoyer le lien de réinitialisation."
        );
      }

      setForgotMessage(
        data?.message ||
          t.resetMailSent ||
          "Si un compte existe avec cet email, un lien a été envoyé."
      );
    } catch (err) {
      setError(err?.message || "Erreur lors de l'envoi.");
    } finally {
      setForgotLoading(false);
    }
  };

  /**
   * ============================================================
   * Synchronisation profil + commandes
   * ============================================================
   *
   * Ce bloc recharge le profil et les commandes
   * dès que l’utilisateur est authentifié.
   */
  useEffect(() => {
    let alive = true;

    if (authStatus === "connected") {
      (async () => {
        try {
          const [profil, orders] = await Promise.all([
            fetchProfilSql(),
            fetchOrdersSql(),
          ]);

          if (!alive) return;

          setUserProfil(profil);
          setEditData(profil);
          setCommandes(orders);
          setIsEditing(false);
        } catch (err) {
          if (!alive) return;
          setError(err.message || "Erreur de synchronisation SQL");
        }
      })();
    }

    return () => {
      alive = false;
    };
  }, [authStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * ============================================================
   * Parsing des items d’une commande
   * ============================================================
   *
   * Ce bloc transforme items en tableau exploitable,
   * qu’il arrive déjà parsé ou en JSON stringifié.
   */
  const parseOrderItems = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== "string") return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  /**
   * ============================================================
   * Total d’une commande en centimes
   * ============================================================
   *
   * Ce bloc normalise le total d’une commande pour l’affichage.
   */
  const getOrderTotalCents = (order) => {
    const items = parseOrderItems(order?.items);
    return normalizeOrderTotalCents(order?.total_ttc, items);
  };

  /**
   * ============================================================
   * Prix d’affichage d’un item
   * ============================================================
   *
   * Ce bloc transforme un prix ligne en montant exploitable
   * par formatCHF().
   */
  const getItemDisplayCents = (item) => {
    const raw = Number(
      item?.appliedPrice ?? item?.price ?? item?.prix ?? item?.unit_price ?? 0
    );

    if (!Number.isFinite(raw) || raw <= 0) {
      return 0;
    }

    return raw < 100 ? Math.round(raw * 100) : Math.round(raw);
  };

  /**
   * ============================================================
   * Feedback local sur une commande
   * ============================================================
   *
   * Ce bloc affiche un message de confirmation au plus près
   * de la commande sur laquelle l’action a eu lieu.
   */
  const pushOrderReorderFeedback = (orderId, message) => {
    if (!orderId || !message) return;

    setReorderFeedbackByOrder((prev) => ({
      ...prev,
      [orderId]: message,
    }));

    window.setTimeout(() => {
      setReorderFeedbackByOrder((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    }, 2500);
  };

  /**
   * ============================================================
   * Réajout produit simple
   * ============================================================
   *
   * Ce bloc déclenche le réajout d’un produit simple
   * et affiche le feedback dans la commande concernée.
   */
  const handleReorderSimpleItemClick = async (orderId, item) => {
    if (typeof onReorderSimpleProduct !== "function") return;

    setError("");

    try {
      const result = await onReorderSimpleProduct(item);

      if (!result?.success) {
        setError(
          result?.message ||
            t.reorderSimpleError ||
            "Impossible d’ajouter ce produit au panier."
        );
        return;
      }

      pushOrderReorderFeedback(
        orderId,
        result?.message || t.reorderSimpleSuccess || "Produit ajouté au panier."
      );
    } catch (err) {
      setError(
        err?.message ||
          t.reorderSimpleError ||
          "Impossible d’ajouter ce produit au panier."
      );
    }
  };

  /**
   * ============================================================
   * Reconfiguration plateau
   * ============================================================
   *
   * Ce bloc redirige vers le configurateur de plateau
   * et affiche un feedback local immédiat.
   */
  const handleReconfigurePlateauClick = async (orderId, item) => {
    if (typeof onReconfigurePlateau !== "function") return;

    setError("");

    try {
      const result = await onReconfigurePlateau(item);

      if (!result?.success) {
        setError(
          result?.message ||
            t.reorderPlateauError ||
            "Impossible d’ouvrir la reconfiguration du plateau."
        );
        return;
      }

      pushOrderReorderFeedback(
        orderId,
        result?.message ||
          t.reorderPlateauRedirect ||
          "Ouverture du configurateur de plateau."
      );
    } catch (err) {
      setError(
        err?.message ||
          t.reorderPlateauError ||
          "Impossible d’ouvrir la reconfiguration du plateau."
      );
    }
  };

  /**
   * ============================================================
   * Ouvrir une commande et aller aux produits
   * ============================================================
   *
   * Ce bloc transforme le CTA global en navigation interne
   * vers le bloc produits de la commande.
   */
  const openOrderToProducts = (orderId) => {
    if (!orderId) return;

    setExpandedOrderId(orderId);

    window.setTimeout(() => {
      const target = document.getElementById(`order-items-${orderId}`);
      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  /**
   * ============================================================
   * Mise à jour du profil
   * ============================================================
   *
   * Ce bloc envoie les coordonnées modifiées au backend
   * puis recharge le profil à jour.
   */
  const handleUpdateProfile = async () => {
    setIsUpdating(true);
    setError("");

    try {
      const payload = {
        prenom: editData.prenom,
        nom: editData.nom,
        telephone: editData.telephone,
        adresse: editData.adresse,
        ville: editData.ville,
        npa: editData.npa,
      };

      const resUpdate = await fetch(`${API_BASE}/update_profil.php`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify(payload),
      });

      if (resUpdate.status === 401) {
        logout();
        throw new Error("Session expirée, veuillez vous reconnecter.");
      }

      const dataUpdate = await resUpdate.json();

      if (dataUpdate.success) {
        const fresh = dataUpdate.user || (await fetchProfilSql());
        setUserProfil(fresh);
        setEditData(fresh);
        setIsEditing(false);
      } else {
        setError(dataUpdate.message || "Erreur de mise à jour.");
      }
    } catch (err) {
      setError(err.message || "Erreur lors de la mise à jour.");
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * ============================================================
   * Reprise de paiement
   * ============================================================
   *
   * Ce bloc redirige l’utilisateur vers un checkout
   * PostFinance pour une commande existante non payée.
   *
   * IMPORTANT
   * ------------------------------------------------------------
   * Cet endpoint backend protège l’accès à la commande via
   * le bearer token utilisateur.
   *
   * Donc, contrairement à un simple fetch public, on doit
   * envoyer le header Authorization comme pour les autres
   * endpoints compte.
   */
  const handlePayExistingOrder = async (orderId) => {
    try {
      setError("");

      const res = await fetch(`${API_BASE}/create_postfinance_checkout.php`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({ order_id: Number(orderId) }),
      });

      if (res.status === 401) {
        logout();
        throw new Error("Session expirée, veuillez vous reconnecter.");
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.success || !data?.url) {
        throw new Error(
          data?.error ||
            data?.message ||
            `Impossible de reprendre le paiement (HTTP ${res.status}).`
        );
      }

      window.location.href = data.url;
    } catch (err) {
      setError(
        err?.message || "Impossible de relancer le paiement de cette commande."
      );
    }
  };

  /**
   * ============================================================
   * Suppression d’une commande non payée
   * ============================================================
   *
   * Ce bloc exécute la suppression côté backend
   * puis retire la commande de l’interface locale.
   */
  const handleDeleteOrder = async (order) => {
    if (!order?.id) return;

    const allowed = canClientDeleteOrder(order);

    if (!allowed) {
      setError(
        t.deleteOrderNotAllowed ||
          "Cette commande ne peut pas encore être supprimée. Une commande non payée devient supprimable 3 jours après sa création."
      );
      return;
    }

    const confirmTemplate =
      t.deleteOrderConfirm ||
      "Voulez-vous vraiment supprimer la commande #{id} ?";

    const confirmed = window.confirm(
      confirmTemplate.replace("{id}", order.id).replace("#{id}", `#${order.id}`)
    );

    if (!confirmed) return;

    try {
      setError("");
      setIsDeletingOrderId(order.id);

      const res = await fetch(`${API_BASE}/delete_order.php`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({ order_id: Number(order.id) }),
      });

      if (res.status === 401) {
        logout();
        throw new Error("Session expirée, veuillez vous reconnecter.");
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.success) {
        throw new Error(
          data?.message || "Impossible de supprimer cette commande."
        );
      }

      setCommandes((prev) =>
        prev.filter((o) => Number(o.id) !== Number(order.id))
      );

      if (expandedOrderId === order.id) {
        setExpandedOrderId(null);
      }
    } catch (err) {
      setError(err?.message || "Erreur lors de la suppression.");
    } finally {
      setIsDeletingOrderId(null);
    }
  };

  /**
   * ============================================================
   * Déconnexion
   * ============================================================
   *
   * Ce bloc délègue à App.js si un handler est fourni,
   * sinon il déclenche la déconnexion directe.
   */
  const handleLogoutClick = () => {
    if (typeof onLogoutRequest === "function") {
      onLogoutRequest();
      return;
    }

    logout();
  };

  /**
   * ============================================================
   * Liste filtrée des commandes
   * ============================================================
   *
   * Ce bloc applique :
   * - le filtre date exact
   * - le tri du plus récent au plus ancien
   */
  const filteredCommandes = useMemo(() => {
    let list = Array.isArray(commandes) ? [...commandes] : [];

    if (selectedOrderDate) {
      list = list.filter((order) => {
        const rawDate = order?.created_at || order?.date_creation || "";
        return extractSqlDate(rawDate) === selectedOrderDate;
      });
    }

    list.sort((a, b) => {
      const ta = getOrderCreatedAtMs(a);
      const tb = getOrderCreatedAtMs(b);
      return tb - ta;
    });

    return list;
  }, [commandes, selectedOrderDate]);

  /**
   * ============================================================
   * Résumé de la carte commandes
   * ============================================================
   *
   * Ce bloc prépare :
   * - le texte de résumé
   * - le texte marketing secondaire de la carte fermée
   */
  const ordersPreviewText = buildOrdersPreview(commandes, t);

  const ordersSecondaryPreview =
    t.orderHistoryMarketingPreview ||
    "Retrouvez vos favoris et commandez-les à nouveau en quelques clics.";

  /**
   * ============================================================
   * Vue reset password
   * ============================================================
   *
   * Ce bloc affiche la vue de réinitialisation
   * si un token est détecté dans l’URL.
   */
  if (resetToken) {
    return (
      <ResetPasswordView
        token={resetToken}
        t={t}
        onDone={() => {
          window.history.replaceState({}, "", window.location.pathname);
          window.location.reload();
        }}
      />
    );
  }

  /**
   * ============================================================
   * Vue compte connecté
   * ============================================================
   *
   * Ce bloc affiche :
   * - l’identité du client
   * - la carte coordonnées
   * - la carte historique des commandes
   * - la déconnexion en bas
   */
  if (authStatus === "connected" && userProfil) {
    return (
      <div className="account-luxury-layout page-transition">
        <div className="account-central-card">
          <header className="account-luxury-header">
            <div className="profile-identity">
              <div className="badge-luxury-status">
                {Number(userProfil.discount) > 0
                  ? `AVANTAGE MEMBRE : -${userProfil.discount}%`
                  : "COMPTE ACTIF : AVANTAGES APPLIQUÉS"}
              </div>

              <h1 className="luxury-title">
                {userProfil.prenom} {userProfil.nom}
              </h1>
            </div>
          </header>

          <div className="account-luxury-grid">
            <AccountAccordionCard
              title={t.myDetails || "VOS COORDONNÉES"}
              preview={
                t.profileCardPreview ||
                "Consulter ou modifier vos informations."
              }
              isOpen={showProfileDetails}
              onToggle={() => setShowProfileDetails((v) => !v)}
              badges={[]}
              actionLabelOpen={t.hideSection || "Masquer"}
              actionLabelClosed={t.showSection || "Afficher"}
            >
              <div className="section-title-area" style={{ marginTop: 0 }}>
                <div></div>

                <button
                  className="edit-toggle-luxury"
                  onClick={() => {
                    setError("");
                    isEditing ? handleUpdateProfile() : setIsEditing(true);
                  }}
                  disabled={isUpdating}
                  type="button"
                >
                  {isUpdating
                    ? "..."
                    : isEditing
                    ? t.saveChanges || "ENREGISTRER"
                    : t.editMyInfo || "MODIFIER MES INFOS"}
                </button>
              </div>

              <div className="form-luxury-body">
                <div className="form-double-row">
                  <Input
                    label={t.firstName || "Prénom"}
                    value={editData.prenom || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setEditData({ ...editData, prenom: e.target.value })
                    }
                  />

                  <Input
                    label={t.lastName || "Nom"}
                    value={editData.nom || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setEditData({ ...editData, nom: e.target.value })
                    }
                  />
                </div>

                <Input
                  label={t.phone || "Téléphone"}
                  value={editData.telephone || ""}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setEditData({ ...editData, telephone: e.target.value })
                  }
                />

                <Input
                  label={t.address || "Adresse"}
                  value={editData.adresse || ""}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setEditData({ ...editData, adresse: e.target.value })
                  }
                />

                <div className="form-double-row">
                  <Input
                    label={t.npa || "Code Postal"}
                    value={editData.npa || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setEditData({ ...editData, npa: e.target.value })
                    }
                  />

                  <Input
                    label={t.city || "Ville"}
                    value={editData.ville || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setEditData({ ...editData, ville: e.target.value })
                    }
                  />
                </div>
              </div>

              {error && <p className="error-feedback">{error}</p>}
            </AccountAccordionCard>

            <div ref={ordersRef}>
              <AccountAccordionCard
                title={t.orderHistory || "HISTORIQUE DES DÉLICES"}
                preview={ordersPreviewText}
                secondaryPreview={ordersSecondaryPreview}
                isOpen={showOrdersSection}
                onToggle={() => setShowOrdersSection((v) => !v)}
                badges={[]}
                actionLabelOpen={t.hideOrders || "Masquer"}
                actionLabelClosed={t.showOrders || "Afficher"}
              >
                {error ? <p className="error-feedback">{error}</p> : null}

                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: "18px",
                  }}
                >
                  <input
                    type="date"
                    value={selectedOrderDate}
                    onChange={(e) => setSelectedOrderDate(e.target.value)}
                    className="luxury-date-input"
                    aria-label="Filtrer les commandes par date exacte"
                  />

                  {selectedOrderDate && (
                    <button
                      type="button"
                      className="edit-toggle-luxury"
                      onClick={() => setSelectedOrderDate("")}
                    >
                      {t.resetDateFilter || "RÉINITIALISER LA DATE"}
                    </button>
                  )}
                </div>

                <div className="luxury-orders-scroller">
                  {filteredCommandes.length > 0 ? (
                    filteredCommandes.map((order) => {
                      const isExpanded = expandedOrderId === order.id;
                      const items = parseOrderItems(order.items);
                      const rawDate =
                        order.created_at || order.date_creation || "";
                      const totalCents = getOrderTotalCents(order);
                      const paid = isPaidStatus(order.status);
                      const unpaid = isUnpaidStatus(order.status);
                      const canDelete = canClientDeleteOrder(order);
                      const preparationNotice = getPreparationNotice(order, t);
                      const localReorderFeedback =
                        reorderFeedbackByOrder[order.id] || "";
                      const reorderCaps = getOrderReorderCapabilities(items);

                      return (
                        <div
                          key={order.id}
                          className={`luxury-order-item ${
                            isExpanded ? "expanded" : ""
                          }`}
                        >
                          <div
                            className="order-summary-row"
                            onClick={() =>
                              setExpandedOrderId(isExpanded ? null : order.id)
                            }
                          >
                            <div className="order-main-info">
                              <span className="order-ref">#{order.id}</span>
                              <span className="order-date">
                                {formatOrderDate(rawDate)}
                              </span>
                            </div>

                            <div className="order-status-price">
                              <span
                                className={`status-pill ${
                                  paid ? "paid" : unpaid ? "pending" : "neutral"
                                }`}
                              >
                                {getOrderStatusLabel(order.status)}
                              </span>

                              <span className="price">
                                {formatCHF(totalCents)}
                              </span>
                            </div>
                          </div>

                          {!isExpanded && reorderCaps.hasAnyReorderable ? (
                            <div
                              style={{
                                marginTop: "12px",
                                display: "flex",
                                justifyContent: "flex-start",
                              }}
                            >
                              <button
                                type="button"
                                className="edit-toggle-luxury"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openOrderToProducts(order.id);
                                }}
                                style={{
                                  minWidth: "320px",
                                }}
                              >
                                {t.reorderSelectionCTA ||
                                  "RECOMMANDER LES PRODUITS DE CETTE SÉLECTION"}
                              </button>
                            </div>
                          ) : null}

                          {!isExpanded && localReorderFeedback ? (
                            <div
                              style={{
                                marginTop: "12px",
                                padding: "12px 14px",
                                borderRadius: "12px",
                                border: "1px solid rgba(191,167,106,.22)",
                                background: "rgba(255,250,240,.92)",
                                color: "#6d552b",
                                lineHeight: 1.5,
                                fontSize: "0.92rem",
                              }}
                            >
                              {localReorderFeedback}
                            </div>
                          ) : null}

                          {isExpanded && (
                            <div className="order-details-expanded page-transition">
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginBottom: "14px",
                                }}
                              >
                                <button
                                  type="button"
                                  className="edit-toggle-luxury"
                                  onClick={() => setExpandedOrderId(null)}
                                >
                                  {t.hideOrder || "MASQUER LA COMMANDE"}
                                </button>
                              </div>

                              {localReorderFeedback ? (
                                <div
                                  style={{
                                    marginBottom: "14px",
                                    padding: "12px 14px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(191,167,106,.22)",
                                    background: "rgba(255,250,240,.92)",
                                    color: "#6d552b",
                                    lineHeight: 1.5,
                                    fontSize: "0.92rem",
                                  }}
                                >
                                  {localReorderFeedback}
                                </div>
                              ) : null}

                              <div
                                id={`order-items-${order.id}`}
                                style={{
                                  marginBottom: "18px",
                                  padding: "16px 18px",
                                  borderRadius: "16px",
                                  border: "1px solid rgba(191,167,106,.18)",
                                  background: "rgba(255,250,240,.72)",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.82rem",
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "#8f7442",
                                    marginBottom: "10px",
                                    fontWeight: 700,
                                  }}
                                >
                                  {t.reorderItemsTitle ||
                                    "Produits à retrouver"}
                                </div>

                                {items.length > 0 ? (
                                  <div className="items-list">
                                    {items.map((item, idx) => {
                                      const itemName =
                                        item?.name ||
                                        item?.product_name ||
                                        item?.productName ||
                                        item?.nom ||
                                        item?.title ||
                                        item?.label ||
                                        `Produit #${item?.id ?? idx + 1}`;

                                      const detailsLabel =
                                        item?.details_label ||
                                        item?.detailsLabel ||
                                        "";

                                      const isPlateau =
                                        isPlateauOrderItem(item);
                                      const canReorderSimple =
                                        isSimpleReorderableItem(item);

                                      return (
                                        <div key={idx} className="item-line">
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "8px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "8px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: "1.04rem",
                                                  fontWeight: 700,
                                                  lineHeight: 1.4,
                                                  color: "#1f1a17",
                                                  wordBreak: "break-word",
                                                }}
                                              >
                                                <strong>
                                                  {item.qty ||
                                                    item.quantite ||
                                                    item.quantity ||
                                                    1}
                                                  ×
                                                </strong>{" "}
                                                {itemName}
                                              </div>

                                              {(Number(item?.grams || 0) > 0 ||
                                                item.variante) && (
                                                <div
                                                  style={{
                                                    fontSize: "0.88rem",
                                                    color: "rgba(0,0,0,.62)",
                                                    lineHeight: 1.4,
                                                  }}
                                                >
                                                  {Number(item?.grams || 0) > 0
                                                    ? `${item.grams}g`
                                                    : ""}
                                                  {Number(item?.grams || 0) >
                                                    0 && item.variante
                                                    ? " — "
                                                    : ""}
                                                  {item.variante
                                                    ? item.variante
                                                    : ""}
                                                </div>
                                              )}

                                              <div
                                                style={{
                                                  fontSize: "0.98rem",
                                                  fontWeight: 700,
                                                  color: "#2a241d",
                                                }}
                                              >
                                                {formatCHF(
                                                  getItemDisplayCents(item)
                                                )}
                                              </div>
                                            </div>

                                            {detailsLabel && (
                                              <div
                                                style={{
                                                  fontSize: "0.85rem",
                                                  opacity: 0.75,
                                                  paddingLeft: "4px",
                                                }}
                                              >
                                                {detailsLabel}
                                              </div>
                                            )}

                                            {(canReorderSimple ||
                                              isPlateau) && (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: "10px",
                                                  marginTop: "2px",
                                                }}
                                              >
                                                {canReorderSimple && (
                                                  <button
                                                    type="button"
                                                    className="edit-toggle-luxury"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleReorderSimpleItemClick(
                                                        order.id,
                                                        item
                                                      );
                                                    }}
                                                    style={{
                                                      minWidth: "200px",
                                                    }}
                                                  >
                                                    {t.reorderAddToCart ||
                                                      "RACHETER"}
                                                  </button>
                                                )}

                                                {isPlateau && (
                                                  <button
                                                    type="button"
                                                    className="edit-toggle-luxury"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleReconfigurePlateauClick(
                                                        order.id,
                                                        item
                                                      );
                                                    }}
                                                    style={{
                                                      minWidth: "260px",
                                                    }}
                                                  >
                                                    {t.reorderPlateau ||
                                                      "RECOMMANDER CE PLATEAU"}
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="empty-msg">
                                    {t.emptyCartData || "Panier indisponible."}
                                  </div>
                                )}
                              </div>

                              {preparationNotice && (
                                <div style={getPreparationNoticeStyle()}>
                                  <strong>
                                    {t.orderReadyTitle || "Commande prête"}
                                    {" : "}
                                  </strong>
                                  {preparationNotice}
                                </div>
                              )}

                              {unpaid && (
                                <div
                                  style={{
                                    marginBottom: "14px",
                                    padding: "12px 14px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(212,175,55,.28)",
                                    background: "rgba(255,248,225,.85)",
                                    color: "#8b4513",
                                    lineHeight: 1.5,
                                    fontSize: "0.92rem",
                                  }}
                                >
                                  <strong>
                                    {t.paymentNotConfirmedTitle ||
                                      "Paiement non confirmé"}
                                    {" : "}
                                  </strong>
                                  {getOrderPaymentNotice(order.status)}

                                  <div
                                    style={{
                                      marginTop: "12px",
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "10px",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="edit-toggle-luxury"
                                      onClick={() =>
                                        handlePayExistingOrder(order.id)
                                      }
                                      style={{ minWidth: "220px" }}
                                    >
                                      {t.payThisOrder || "PAYER MA COMMANDE"}
                                    </button>

                                    {canDelete && (
                                      <button
                                        type="button"
                                        className="edit-toggle-luxury"
                                        onClick={() => handleDeleteOrder(order)}
                                        disabled={
                                          isDeletingOrderId === order.id
                                        }
                                        style={{
                                          minWidth: "220px",
                                          background: "#fff5f5",
                                          border:
                                            "1px solid rgba(176,0,32,.18)",
                                          color: "#8f1730",
                                        }}
                                      >
                                        {isDeletingOrderId === order.id
                                          ? t.deletingOrder || "SUPPRESSION..."
                                          : t.deleteOrder ||
                                            "SUPPRIMER LA COMMANDE"}
                                      </button>
                                    )}
                                  </div>

                                  {!canDelete && (
                                    <div
                                      style={{
                                        marginTop: "12px",
                                        fontSize: "0.85rem",
                                        color: "rgba(0,0,0,.68)",
                                        lineHeight: 1.45,
                                      }}
                                    >
                                      {t.deleteOrderInfo ||
                                        "Cette commande non payée pourra être supprimée 3 jours après sa création."}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div
                                style={{
                                  marginTop: "22px",
                                  paddingTop: "18px",
                                  borderTop: "1px solid rgba(0,0,0,.08)",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.78rem",
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                    color: "rgba(0,0,0,.48)",
                                    marginBottom: "14px",
                                    fontWeight: 700,
                                  }}
                                >
                                  {t.orderDetailsSecondaryTitle ||
                                    "Détails de cette commande"}
                                </div>

                                <div className="order-details-grid">
                                  <div className="detail-line">
                                    <span className="detail-label">
                                      {t.orderNumber || "Numéro de commande"}
                                    </span>
                                    <span className="detail-value">
                                      #{order.id}
                                    </span>
                                  </div>

                                  <div className="detail-line">
                                    <span className="detail-label">
                                      {paid
                                        ? t.pricePaid || "Prix payé"
                                        : t.orderTotal ||
                                          "Montant de la commande"}
                                    </span>
                                    <span className="detail-value">
                                      {formatCHF(totalCents)}
                                    </span>
                                  </div>

                                  <div className="detail-line">
                                    <span className="detail-label">
                                      {t.status || "Statut"}
                                    </span>
                                    <span
                                      className={`detail-value status-pill ${
                                        paid
                                          ? "paid"
                                          : unpaid
                                          ? "pending"
                                          : "neutral"
                                      }`}
                                    >
                                      {getOrderStatusLabel(order.status)}
                                    </span>
                                  </div>

                                  <div className="detail-line">
                                    <span className="detail-label">
                                      {t.deliveryMode || "Mode de récupération"}
                                    </span>
                                    <span className="detail-value">
                                      {order.delivery_mode === "livraison"
                                        ? "📦 Livraison"
                                        : "🛍️ Pick & Pay"}
                                    </span>
                                  </div>

                                  <div className="detail-line">
                                    <span className="detail-label">
                                      {t.orderDate || "Date de la commande"}
                                    </span>
                                    <span className="detail-value">
                                      {formatOrderDate(rawDate)}
                                    </span>
                                  </div>

                                  <div className="detail-line detail-line-full">
                                    <span className="detail-label">
                                      {t.specialInstructions ||
                                        "Instructions particulières"}
                                    </span>
                                    <span className="detail-value">
                                      {order.notes?.trim() ? order.notes : "—"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="empty-msg">
                      {selectedOrderDate
                        ? t.noOrdersForThisDate ||
                          "Aucune commande pour cette date."
                        : t.noOrdersYet || "Aucune commande pour le moment."}
                    </p>
                  )}
                </div>
              </AccountAccordionCard>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "6px",
              }}
            >
              <button className="btn-logout-luxury" onClick={handleLogoutClick}>
                {t.logout || "DÉCONNEXION"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * ============================================================
   * Vue inscription
   * ============================================================
   *
   * Ce bloc affiche le formulaire d’inscription.
   */
  
  if (isRegistering) {
    return (
      <FormulaireInscription
        t={t}
        onToggleMode={() => setIsRegistering(false)}
        onRegisterSuccess={registerSql}
      />
    );
  }

  /**
   * ============================================================
   * Vue mot de passe oublié
   * ============================================================
   *
   * Ce bloc affiche le formulaire d’envoi du lien de reset.
   */
  if (isForgotPassword) {
    return (
      <div className="auth-luxury-container page-transition">
        <div className="auth-central-card">
          <div className="auth-header">
            <h1>{t.forgotPasswordTitle || "Mot de passe oublié"}</h1>
            <p>
              {t.forgotPasswordSubtitle ||
                "Recevez un lien de réinitialisation par email."}
            </p>
          </div>

          <form onSubmit={handleForgotPassword} className="auth-form-luxury">
            <Input
              label={t.email || "Email"}
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
            />

            {error && <p className="auth-error">{error}</p>}
            {forgotMessage && <p className="auth-success">{forgotMessage}</p>}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginTop: "10px",
              }}
            >
              <Button
                type="submit"
                variant="gold"
                fullWidth
                disabled={forgotLoading}
              >
                {forgotLoading
                  ? t.processing || "Traitement..."
                  : t.sendResetLink || "ENVOYER LE LIEN DE RÉINITIALISATION"}
              </Button>

              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={() => {
                  setIsForgotPassword(false);
                  setError("");
                  setForgotMessage("");
                }}
              >
                {t.backToLogin || "RETOUR À LA CONNEXION"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  /**
   * ============================================================
   * Vue connexion
   * ============================================================
   *
   * Ce bloc affiche le formulaire de connexion
   * lorsqu’aucune session active n’est présente.
   */
  return (
    <div className="auth-luxury-container page-transition">
      <div className="auth-central-card">
        <div className="auth-header">
          <h1>{t.myAccount || "Mon Compte"}</h1>
          <p>{t.accessExclusive || "Accédez à votre univers Marché Deluxe"}</p>
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError("");

            try {
              const user = await login(loginData);
              if (user) setUserProfil(user);
            } catch (err) {
              setError(err.message || "Identifiants incorrects");
            }
          }}
          className="auth-form-luxury"
        >
          <Input
            label={t.email || "Email"}
            type="email"
            value={loginData.email}
            onChange={(e) =>
              setLoginData({ ...loginData, email: e.target.value })
            }
            required
          />

          <Input
            label={t.password || "Mot de passe"}
            type="password"
            value={loginData.password}
            onChange={(e) =>
              setLoginData({ ...loginData, password: e.target.value })
            }
            required
          />

          {error && <p className="auth-error">{error}</p>}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            <Button
              type="submit"
              variant="gold"
              fullWidth
              disabled={fetchStatus === "loading"}
            >
              {fetchStatus === "loading"
                ? "..."
                : t.loginAction || "SE CONNECTER"}
            </Button>

            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => {
                setIsForgotPassword(true);
                setError("");
                setForgotMessage("");
              }}
            >
              {t.forgotPassword || "MOT DE PASSE OUBLIÉ ?"}
            </Button>

            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => setIsRegistering(true)}
            >
              {t.createAccount || "CRÉER UN COMPTE"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountView;
